import React, { useState, useContext } from "react";
import { ThemeContext } from "styled-components";

import { useSiteConfig } from "components/Site/Contexts/SiteConfig";
import {
  MobileDrawerContainer,
  IconWrapper,
  LinkBar,
  LinkBarItem,
  LinkBarExternalLink,
  LinkBarInternalLink
} from "./styles";
import CloseModalIcon from "icons/CloseModalIcon";
import ExternalHomeIcon from "icons/ExternalHomeIcon";
import InformationCircleIcon from "icons/InformationCircleIcon";
import NavigationMenuVerticalIcon from "icons/NavigationMenuVerticalIcon";
import FacebookIcon from "icons/FacebookIcon";
import TwitterIcon from "icons/TwitterIcon";
import { Site } from "api/typedefs/Site";

interface TopSectionProps {
  site: Site;
  iconColor: string;
  open: boolean;
  toggleOpen: () => void;
}

const TopSection = ({ site, iconColor, open, toggleOpen }: TopSectionProps) => {
  const { linkWebsite, description, linkTwitter, linkFacebook } = site;
  const hasLinks =
    [linkWebsite, description, linkTwitter, linkFacebook].filter(x => !!x)
      .length > 0;

  return (
    <LinkBar className="linkbar--top">
      {hasLinks && (
        <LinkBarItem onClick={toggleOpen}>
          {open ? (
            <CloseModalIcon size={24} color={iconColor} />
          ) : (
            <NavigationMenuVerticalIcon size={24} color={iconColor} />
          )}
        </LinkBarItem>
      )}

      {open && (
        <>
          {site.linkWebsite && (
            <LinkBarExternalLink
              href={site.linkWebsite}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalHomeIcon size={24} color={iconColor} />
            </LinkBarExternalLink>
          )}
          {site.description && (
            <LinkBarInternalLink to="/info">
              <InformationCircleIcon size={24} color={iconColor} />
            </LinkBarInternalLink>
          )}
          {site.linkTwitter && (
            <LinkBarExternalLink
              href={site.linkTwitter}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TwitterIcon size={24} color={iconColor} />
            </LinkBarExternalLink>
          )}
          {site.linkFacebook && (
            <LinkBarExternalLink
              href={site.linkFacebook}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FacebookIcon size={24} color={iconColor} />
            </LinkBarExternalLink>
          )}
        </>
      )}
    </LinkBar>
  );
};

interface MobileDrawerChildren {
  children: (args: {
    InternalLink: React.ReactNode;
    ExternalLink: React.ReactNode;
    Action: React.ReactNode;
    iconColor: string;
  }) => React.ReactElement;
}

interface BottomSectionProps extends MobileDrawerChildren {
  iconColor: string;
}

const BottomSection: React.FC<BottomSectionProps> = ({
  iconColor,
  children
}) => (
  <LinkBar className="linkbar--bottom">
    {children({
      InternalLink: LinkBarInternalLink,
      ExternalLink: LinkBarExternalLink,
      Action: LinkBarItem,
      iconColor
    })}
  </LinkBar>
);

interface MobileDrawerProps extends MobileDrawerChildren {
  icon: React.ReactNode;
  toggleDrawer: () => void;
  anchor: "left" | "right";
  className: string;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  children: actions,
  icon = null,
  toggleDrawer,
  anchor = "left",
  ...restProps
}) => {
  const site = useSiteConfig();
  const theme = useContext(ThemeContext);
  const iconColor = theme.colors.isLight ? "black" : "white";
  const [topSectionOpen, setTopSectionOpen] = useState(false);
  const toggleTopSectionOpen = () => setTopSectionOpen(!topSectionOpen);

  return (
    <MobileDrawerContainer anchor={anchor} {...restProps}>
      <IconWrapper>{icon}</IconWrapper>
      <TopSection
        site={site}
        iconColor={iconColor}
        open={topSectionOpen}
        toggleOpen={toggleTopSectionOpen}
      />
      {!topSectionOpen && (
        <BottomSection iconColor={iconColor}>{actions}</BottomSection>
      )}
    </MobileDrawerContainer>
  );
};
